import React, { useState, useEffect, useRef } from 'react';
import { todosAPI } from '../services/api';
import stateManager from '../services/stateManager';
import Todo from './Todo';
import { ChartIcon } from './Icons';
import styles from '../styles/ProgressBar.module.css';

/**
 * ProgressBar - Contextual progress display at top of chat
 * - Shows mission objective, stage wizard, and current todo
 * - Cycles through incomplete todos every 4 seconds
 * - Click to navigate to full Tasks view
 */
function ProgressBar({ conversationId, onNavigateToTodos, onNavigateToDashboard, onTodoComplete }) {
  const [progressData, setProgressData] = useState(null);
  const [error, setError] = useState(null);
  const [currentTodoIndex, setCurrentTodoIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const todoRotationTimerRef = useRef(null);
  const progressCacheRef = useRef(null);
  const lastFetchTimeRef = useRef(0);

  // Load from StateManager on mount
  useEffect(() => {
    const cached = stateManager.getProgressData();
    if (cached) {
      progressCacheRef.current = cached;
      setProgressData(cached);
    }
  }, []);

  // Fetch progress data with caching (cache for 30 seconds)
  const fetchProgress = async (forceRefresh = false) => {
    if (!conversationId) return;

    const now = Date.now();
    const cacheAge = now - lastFetchTimeRef.current;
    const CACHE_DURATION = 30 * 1000; // 30 seconds

    // Use cached data if available and fresh
    if (!forceRefresh && progressCacheRef.current && cacheAge < CACHE_DURATION) {
      setProgressData(progressCacheRef.current);
      return;
    }

    try {
      const data = await todosAPI.getProgressSummary(conversationId);
      progressCacheRef.current = data;
      lastFetchTimeRef.current = now;
      setProgressData(data);
      setError(null);

      // Save to StateManager
      stateManager.setProgressData(data);
    } catch (err) {
      console.error('[ProgressBar] Failed to fetch progress:', err);
      setError(err.message);
      // Use cached data on error if available
      if (progressCacheRef.current) {
        setProgressData(progressCacheRef.current);
      }
    }
  };

  // Initialize: fetch data (no flash animation)
  useEffect(() => {
    if (conversationId) {
      // Always force refresh when conversationId changes (objective change scenario)
      progressCacheRef.current = null;
      lastFetchTimeRef.current = 0;
      fetchProgress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Subscribe to todos changes from singleton - refresh from server
  useEffect(() => {
    const unsubscribe = stateManager.subscribe('todos', (updatedTodos) => {
      console.log('[ProgressBar] Todos updated via stateManager - refreshing progress from server');

      if (!conversationId) {
        console.warn('[ProgressBar] Ignoring update - no conversation ID');
        return;
      }

      // Invalidate cache and fetch fresh progress data from server
      progressCacheRef.current = null;
      lastFetchTimeRef.current = 0;
      fetchProgress(true);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Also listen for todosRefreshed custom event (backup listener for tool operations)
  useEffect(() => {
    const handleTodosRefreshed = () => {
      console.log('[ProgressBar] Todos refreshed via custom event - forcing progress refresh');
      if (conversationId) {
        // Force refresh by invalidating cache and fetching
        progressCacheRef.current = null;
        lastFetchTimeRef.current = 0;
        fetchProgress(true);
      }
    };

    window.addEventListener('todosRefreshed', handleTodosRefreshed);
    return () => window.removeEventListener('todosRefreshed', handleTodosRefreshed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  /**
   * User manually clicked the bar - navigate to tasks view
   */
  const handleBarClick = () => {
    if (onNavigateToTodos) {
      onNavigateToTodos();
    }
  };

  // Calculate incomplete todos count for dependency tracking
  const incompleteTodosCount = progressData?.currentStageTodos?.filter(t => !t.is_completed).length || 0;

  // Reset index when incomplete todos list changes (e.g., when todos are completed)
  useEffect(() => {
    if (incompleteTodosCount > 0 && currentTodoIndex >= incompleteTodosCount) {
 //     // console.log('[ProgressBar] Resetting todo index from', currentTodoIndex, 'to 0 (incomplete count:', incompleteTodosCount, ')');
      setCurrentTodoIndex(0);
    }
  }, [incompleteTodosCount, currentTodoIndex]);

  // Rotate through todos every 4 seconds
  useEffect(() => {
    // Clear any existing rotation timer
    if (todoRotationTimerRef.current) {
      clearInterval(todoRotationTimerRef.current);
      todoRotationTimerRef.current = null;
    }

    if (incompleteTodosCount > 1) {
   //   // console.log('[ProgressBar] Starting todo rotation for', incompleteTodosCount, 'incomplete todos');
      todoRotationTimerRef.current = setInterval(() => {
        setCurrentTodoIndex(prev => (prev + 1) % incompleteTodosCount);
      }, 4000);

      return () => {
        if (todoRotationTimerRef.current) {
          clearInterval(todoRotationTimerRef.current);
        }
      };
    }
  }, [incompleteTodosCount]);

  // Trigger slide-in animation when data loads
  useEffect(() => {
    if (progressData) {
      // Small delay to ensure smooth animation
      setTimeout(() => setIsVisible(true), 50);
    }
  }, [progressData]);

  // Detect scroll to minimize progress bar (with debouncing to prevent flickering)
  useEffect(() => {
    const chatArea = document.querySelector('[class*="chatArea"]');
    if (!chatArea) return;

    let scrollTimeout;
    const handleScroll = () => {
      // Clear any pending timeout to debounce rapid scroll events
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Debounce scroll handling to prevent flickering
      scrollTimeout = setTimeout(() => {
        const scrollTop = chatArea.scrollTop;

        // Add hysteresis: different thresholds for minimizing vs expanding
        // This prevents rapid toggling at the boundary
        if (scrollTop > 100) {
          setIsMinimized(true);
        } else if (scrollTop < 60) {
          setIsMinimized(false);
        }
        // Between 60-100px, maintain current state (no change)
      }, 50); // 50ms debounce delay
    };

    chatArea.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      chatArea.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  // Don't render until we have actual data
  if (!progressData) {
    return null;
  }

  const {
    currentStage = 1,
    totalStages = 4,
    currentStageTodos = [],
    stageProgress = { completed: 0, total: 1 },
    currentStageTitle = 'Stage 1',
    missionObjective = null
  } = progressData;

  // Don't render if there are no stages (invalid state - user should be in onboarding)
  if (totalStages === 0) {
    return null;
  }

  // Debug: log what we're rendering
 // // console.log('[ProgressBar] RENDERING with stageProgress:', stageProgress.completed, '/', stageProgress.total);

  // Get incomplete todos for cycling display
  const incompleteTodos = currentStageTodos?.filter(t => !t.is_completed) || [];
  const currentDisplayTodo = incompleteTodos.length > 0
    ? incompleteTodos[currentTodoIndex % incompleteTodos.length]
    : null;

  // Check if all required todos are complete across all stages
  const allTodosComplete = progressData.overallProgress === 100 && incompleteTodos.length === 0;

  return (
    <div
      className={`${styles.container} ${isVisible ? styles.visible : ''} ${allTodosComplete ? styles.completed : ''} ${isMinimized ? styles.minimized : ''}`}
      data-tour="progress-bar"
    >
      {/* Redesigned progress bar */}
      <div className={styles.progressBarWrapper} onClick={handleBarClick}>
        {allTodosComplete ? (
          /* Completion state - show trophy and celebration */
          <div className={styles.completionState}>
            <div className={styles.completionIcon}>
              <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                <g strokeWidth="0"></g>
                <g strokeLinecap="round" strokeLinejoin="round"></g>
                <g>
                  <path d="M927.165022 235.879629c-3.352201-17.861679-17.085574-47.83797-71.465148-47.83797h-12.323484c-0.485322-4.473355-0.758699-9.128962-1.793847-13.12109-7.527605-29.06708-29.670149-45.730814-60.757355-45.730814H232.721967c-31.087206 0-53.22975 16.664758-60.757355 45.730814-1.03822 4.008511-1.315693 8.682548-1.801014 13.178428-54.220871 1.053578-68.480522 32.244197-72.020094 51.065257-18.483178 98.677972 174.753676 324.021822 334.206409 393.781176v112.843426h-70.392116c-21.665413 0-39.228118 17.562705-39.228119 39.228118v109.620235H690.8185V785.016974c0-21.665413-17.562705-39.228118-39.228118-39.228118h-70.392116V627.944774c153.143552-73.008143 364.509319-293.092294 345.966756-392.065145z m-38.557474 7.230679c8.666165 46.260163-72.333403 156.069816-172.334235 244.716783 63.688739-85.712512 114.859456-188.020158 125.760763-260.557313h13.666821c29.937383 0 32.101877 11.501304 32.906651 15.84053z m-751.906571 3.275409c1.076103-5.68154 3.684963-18.796487 34.802887-19.180444 11.489017 76.582527 67.800662 186.606172 136.417361 274.961332-99.576943-88.66335-180.725999-204.985938-171.220248-255.780888z m514.889405 558.245828v50.778569H361.956773v-50.778569c0-10.832707 8.781864-19.614571 19.614571-19.614571h90.006687V599.842194l-10.094485-5.611916C338.837442 526.135761 189.433119 263.921801 209.947686 184.756011c2.604764-10.08527 7.316685-16.339162 22.774281-16.339162h548.103221c15.457597 0 20.169517 6.253892 22.793735 16.339162 20.495113 79.175004-128.90921 341.379749-251.555314 409.473243l-10.094486 5.611916v185.175804H631.975811c10.833731 0 19.614571 8.781864 19.614571 19.614571z" fill="#f0c800"></path>
                  <path d="M589.625866 801.470811H413.098821c-10.832707 0-19.614571 8.780841-19.614571 19.614571 0 10.831683 8.780841 19.614571 19.614571 19.614572h176.527045c10.832707 0 19.614571-8.781864 19.614571-19.614572 0-10.833731-8.780841-19.614571-19.614571-19.614571zM563.102117 456.480842c10.189707 13.514262 19.581807 28.130225 28.007359 43.772122 15.625514-11.55045 30.702225-24.665397 44.972115-39.296719-10.180492-15.2477-21.215928-29.389604-32.931224-42.367351-12.618363 14.094805-26.056858 26.743885-40.04825 37.891948zM764.867935 199.921801l-57.076488 4.16107c-11.433727 80.91049-44.340378 149.756539-87.709088 200.365143 11.929288 12.89174 23.195098 26.957877 33.624394 42.139024 56.174444-60.047803 98.898107-145.061001 111.718175-246.316092-0.185323-0.12389-0.372694-0.226279-0.556993-0.349145z" fill="#ffe7a3"></path>
                </g>
              </svg>
            </div>
            <div className={styles.completionContent}>
              <div className={styles.completionTitle}>Mission Complete!</div>
              <div className={styles.completionSubtitle}>
                {missionObjective || 'Your Financial Journey'}
              </div>
            </div>
          </div>
        ) : (
          /* Normal state - show progress */
          <>
            {/* Prominent objective with icon */}
            <div className={styles.objectiveSection}>
              <svg className={styles.mountainIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 20L9 8L13 14L18 6L21 20H3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
                <path d="M9 8L13 14L18 6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
              </svg>
              <div className={styles.objectiveContent}>
                {isMinimized ? (
                  <div className={styles.objectiveText}>
                    <span className={styles.objectiveBold}>{missionObjective || 'Your Financial Journey'}</span>
                    <span className={styles.objectiveLight}> - ({currentStage}) {currentStageTitle}</span>
                  </div>
                ) : (
                  <>
                    <div className={styles.objectiveText}>
                      {missionObjective || 'Your Financial Journey'}
                    </div>
                    {currentStageTitle && (
                      <div className={styles.objectiveSubtitle}>
                        {currentStageTitle}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Dashboard icon button */}
            {onNavigateToDashboard && (
              <button
                className={styles.dashboardButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToDashboard();
                }}
                aria-label="View Progress Dashboard"
                title="View Progress Dashboard"
              >
                <ChartIcon size={16} />
              </button>
            )}

            {/* Wizard steps with integrated circular progress */}
            <div className={styles.progressSection}>
              <div className={styles.wizardSteps}>
                {Array.from({ length: totalStages }, (_, i) => i + 1).map((stage) => {
                  // Calculate circle progress for this stage
                  const circleRadius = 15;
                  const circleCircumference = 2 * Math.PI * circleRadius;
                  const progressRatio = stageProgress.completed / stageProgress.total;
                  const dashOffset = circleCircumference * (1 - progressRatio);

                  if (stage === currentStage && !allTodosComplete) {
               //     // console.log('[ProgressBar] Rendering circle for stage', stage, '- ratio:', progressRatio, 'dashOffset:', dashOffset);
                  }

                  return (
                  <div key={stage} className={styles.stepWrapper}>
                    {stage === currentStage ? (
                      // Current stage: show circular progress
                      <div className={styles.circularProgress}>
                        <svg className={styles.progressRing} viewBox="0 0 36 36">
                          <circle
                            className={styles.progressRingCircle}
                            stroke="rgba(110, 142, 135, 0.15)"
                            strokeWidth="3"
                            fill="transparent"
                            r="15"
                            cx="18"
                            cy="18"
                          />
                          <circle
                            className={styles.progressRingCircleFill}
                            stroke="#6e8e87"
                            strokeWidth="3"
                            fill="transparent"
                            r="15"
                            cx="18"
                            cy="18"
                            style={{
                              strokeDasharray: `${circleCircumference}`,
                              strokeDashoffset: `${dashOffset}`
                            }}
                          />
                        </svg>
                        <div className={styles.circularText}>
                          {stage}
                        </div>
                      </div>
                    ) : (
                      // Completed or locked stages: show regular step
                      <div
                        className={`${styles.step} ${
                          stage < currentStage ? styles.stepCompleted : styles.stepLocked
                        }`}
                      >
                        {stage < currentStage ? '✓' : stage}
                      </div>
                    )}
                    {stage < totalStages && (
                      <div className={`${styles.stepLine} ${stage < currentStage ? styles.stepLineCompleted : ''}`} />
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Current todo cycling display */}
            {currentDisplayTodo && (
              <Todo
                key={currentTodoIndex}
                todoId={currentDisplayTodo.id}
                todoText={currentDisplayTodo.todo_text}
                isCompleted={currentDisplayTodo.is_completed}
                isOptional={currentDisplayTodo.is_optional}
                variant="progress"
              />
            )}
          </>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
}

export default ProgressBar;
